import React, { useState, useEffect, useRef } from "react";
import "react-grid-layout/css/styles.css";
import { cloneDeep } from "lodash";
import RGL, { WidthProvider } from "react-grid-layout";
import { v4 as uuidv4 } from "uuid";
import useDataSourceManager from "hooks/useDataSourceManager";

// New Surveillance Components
import SiteNetworkStatus from "./gridComponents/SiteNetworkStatus.js";
import EventLog from "./gridComponents/EventLog.js";
import AlertLog from "./gridComponents/AlertLog.js";
import DetectedEventTrends from "./gridComponents/DetectedEventTrends.js";
import SystemLog from "./gridComponents/SystemLog.js";
import PeopleFlowStatistics from "./gridComponents/PeopleFlowStatistics.js";
import TrafficStatistics from "./gridComponents/TrafficStatistics.js";
import NvrInfo from "./gridComponents/NvrInfo.js";

import { defaultWidgetCard, ENABLED_WIDGET_KEYS, widgetKeyMap } from "../const";

const ReactGridLayout = WidthProvider(RGL);
const checkMockup = process.env.REACT_APP_IS_MOCKUP;

const GridLayout = (props) => {
  const {
    statsData = {},
    fetchDataMode,
    setFetchDataMode,
    targetWidget,
    widgetSet,
    setWidgetSet,
    actionStatus,
    setShowMenu,
    openDeviceDrawer,
  } = props;

  const [widgetUuid] = useState(uuidv4());
  const [updateUuid] = useState(uuidv4());
  const { dataMap, getData, sendRequest } = useDataSourceManager();
  const rawBlocksRef = useRef([]);

  const [allowDrag, setAllowDrag] = useState(false);
  const [sortable, setSortable] = useState(false);

  // Filter default cards by enabled widgets
  const enabledGridIds = ENABLED_WIDGET_KEYS.map((k) => widgetKeyMap[k]).filter(Boolean);
  const filteredDefaultWidgetCard = defaultWidgetCard.filter((card) => enabledGridIds.includes(card.i));

  const [originalLayout, setOriginalLayout] = useState(cloneDeep(filteredDefaultWidgetCard));
  const [layout, setLayout] = useState([]);
  const [gridKey, setGridKey] = useState(0);
  const [ifChange, setIfChange] = useState(false);
  const [hasFirstRender, setHasFirstRender] = useState(false);

  // Synchronize customize action state
  useEffect(() => {
    if (actionStatus) {
      if (actionStatus.draggable) {
        setSortable(true);
        setAllowDrag(true);
      } else {
        setSortable(false);
        setAllowDrag(false);
        if (actionStatus.update.custom) {
          if (ifChange === true) {
            updateLayout();
            setIfChange(false);
          }
        } else if (actionStatus.update.system) {
          if (ifChange === true) {
            updateLayout();
            setIfChange(false);
          }
        } else {
          if (actionStatus.reset.custom) {
            if (ifChange === true) {
              const originData = cloneDeep(originalLayout);
              setLayout(originData);
              setIfChange(false);
            }
          } else if (actionStatus.reset.system) {
            initLayout();
          }
        }
      }
    }
  }, [actionStatus]);

  // Synchronize customized widget checkbox selections
  useEffect(() => {
    if (targetWidget && Object.keys(targetWidget).length > 0) {
      setIfChange(true);
      const isVisible = targetWidget.check;
      const targetId = targetWidget.id;

      if (isVisible) {
        // Recover item from filteredDefaultWidgetCard
        const findOriginItem = filteredDefaultWidgetCard.find((item) => item.i === targetId);
        if (findOriginItem) {
          setLayout((prev) => [...prev, findOriginItem]);
        }
      } else {
        // Remove item
        setLayout((prev) => prev.filter((item) => item.i !== targetId));
      }
    }
  }, [targetWidget]);

  const onLayoutChange = (newLayout) => {
    const sortedLayout = newLayout.sort((a, b) => a.y - b.y || a.x - b.x);
    setLayout(sortedLayout);
    if (hasFirstRender) {
      setIfChange(true);
    } else {
      setHasFirstRender(true);
    }
  };

  useEffect(() => {
    if (fetchDataMode === "fetchDashboardLayout") {
      if (checkMockup === "false") {
        getWidgetData();
      } else {
        setShowMenu(true);
        setGridKey((prev) => prev + 1);
        initLayout();
      }
    }
  }, [statsData, fetchDataMode]);

  useEffect(() => {
    const response = getData(widgetUuid);
    if (response && response.data) {
      setShowMenu(true);
      const blocks = response.data.blocks || [];
      rawBlocksRef.current = blocks;
      const hasSurveillanceWidgets = blocks.some((block) => enabledGridIds.includes(block.blockId));

      if (!hasSurveillanceWidgets) {
        initLayout();
      } else {
        const formatLayout = makeLayoutData(blocks);

        setOriginalLayout(formatLayout);
        setGridKey((prev) => prev + 1);
        setLayout(formatLayout);

        const selectedMenu = checkMenuSetting(formatLayout);
        setWidgetSet(selectedMenu);
        setFetchDataMode("intervalFetch");
      }
    }
  }, [dataMap]);

  const getWidgetData = async () => {
    const tmpResource = "users/surveillanceDashboard.read";
    sendRequest(tmpResource, widgetUuid);
  };

  const setWidgetData = async (updateList) => {
    if (checkMockup === "true") {
      return;
    }
    const tmpResource = "users/surveillanceDashboard.update";
    const tmpParam = { blocks: updateList };
    sendRequest(tmpResource, updateUuid, null, { param: tmpParam });
  };

  const updateLayout = async () => {
    const updateList = cloneDeep(layout);
    
    // Filter out Surveillance widgets to keep Network widgets
    const otherDashboardBlocks = rawBlocksRef.current.filter(
      (block) => !enabledGridIds.includes(block.blockId)
    );

    const currentDashboardBlocks = [];
    updateList.forEach((item) => {
      const obj = {
        blockId: item.i,
        x: item.x,
        y: item.y,
        visible: true,
      };
      currentDashboardBlocks.push(obj);
    });

    const resultList = [...otherDashboardBlocks, ...currentDashboardBlocks];

    setWidgetData(resultList);
    setOriginalLayout(updateList);
    setGridKey((prev) => prev + 1);
    rawBlocksRef.current = resultList;
  };

  const makeLayoutData = (list) => {
    const defaultLayout = cloneDeep(originalLayout);
    const systemLayout = cloneDeep(list).filter((item) => enabledGridIds.includes(item.blockId));
    const formatLayout = [];

    systemLayout.forEach((subSystem) => {
      const tmpObj = {
        i: subSystem.blockId,
        w: "",
        h: "",
        x: "",
        y: subSystem.y,
      };

      defaultLayout.forEach((subDefault) => {
        if (tmpObj.i === subDefault.i) {
          tmpObj.w = subDefault.w;
          tmpObj.h = subDefault.h;
          tmpObj.x = subDefault.x;
        }
      });
      formatLayout.push(tmpObj);
    });
    return formatLayout;
  };

  const checkMenuSetting = (list) => {
    const selectedMenu = cloneDeep(widgetSet);
    for (const key in selectedMenu) {
      if (key === "siteNetwork") {
        selectedMenu[key].check = true;
      } else {
        selectedMenu[key].check = false;
      }
    }
    list.forEach((subSystem) => {
      for (const key in selectedMenu) {
        if (selectedMenu[key].id === subSystem.i) {
          selectedMenu[key].check = true;
        }
      }
    });
    return selectedMenu;
  };

  const initLayout = () => {
    const originData = cloneDeep(filteredDefaultWidgetCard);
    const formatLayout = originData.map((item) => ({
      i: item.i,
      w: item.w,
      h: item.h,
      x: item.x,
      y: item.y,
    }));

    setOriginalLayout(formatLayout);
    setLayout(formatLayout);

    const allSelectedMenu = cloneDeep(widgetSet);
    for (const key in widgetSet) {
      if (ENABLED_WIDGET_KEYS.includes(key)) {
        allSelectedMenu[key].check = true;
      }
    }
    setWidgetSet(allSelectedMenu);
  };

  return (
    <>
      <div style={{ padding: "0px" }}>
        <ReactGridLayout
          key={gridKey}
          className="layout"
          layout={layout}
          cols={6}
          rowHeight={0}
          margin={[20, 20]}
          onLayoutChange={onLayoutChange}
          isResizable={false}
          isDraggable={allowDrag}
        >
          {
            layout.map((item) => {
              if (item.i === "site-network") {
                return (
                  <div key={item.i}>
                    <SiteNetworkStatus
                      sortable={sortable}
                      surveillanceStatus={statsData?.surveillanceStatus}
                    />
                  </div>
                );
              }
              if (item.i === "event-log") {
                return (
                  <div key={item.i}>
                    <EventLog sortable={sortable} events={statsData?.events} />
                  </div>
                );
              }
              if (item.i === "alert-log") {
                return (
                  <div key={item.i}>
                    <AlertLog sortable={sortable} alerts={statsData?.alerts} />
                  </div>
                );
              }
              if (item.i === "event-trends") {
                return (
                  <div key={item.i}>
                    <DetectedEventTrends sortable={sortable} dataset={statsData?.eventTrends} />
                  </div>
                );
              }
              if (item.i === "system-log") {
                return (
                  <div key={item.i}>
                    <SystemLog sortable={sortable} logs={statsData?.logs} />
                  </div>
                );
              }
              if (item.i === "people-flow") {
                return (
                  <div key={item.i}>
                    <PeopleFlowStatistics sortable={sortable} dataset={statsData?.peopleFlow} />
                  </div>
                );
              }
              if (item.i === "traffic-stats") {
                return (
                  <div key={item.i}>
                    <TrafficStatistics sortable={sortable} dataset={statsData?.traffic} />
                  </div>
                );
              }
              if (item.i === "nvr-info") {
                return (
                  <div key={item.i}>
                    <NvrInfo sortable={sortable} nvrs={statsData?.nvrs} />
                  </div>
                );
              }
              return null;
            })
          }
        </ReactGridLayout>
      </div>
    </>
  );
};

export default GridLayout;